// Числовые константы правил (v1.1). Единственное место, где они записаны, — в коде только ссылки на них.
import type { CardId } from '../types.ts';

/**
 * Версия правил движка. Партия помнит версию, на которой началась (G.rulesVersion), и движок играет её по ТЕМ
 * правилам до конца: цены пакета ребаланса, ветки эффектов и базовые правки берутся по версии партии, а не по
 * текущей. Поэтому журнал любой партии воспроизводится с нуля независимо от того, сколько обновлений вышло
 * с тех пор (шаг назад, записи для разбора), а партия, продолженная после обновления, не меняет правил на ходу.
 *
 * Поднимать при ЛЮБОЙ правке, меняющей ход партии: цены/эффекты режима, базовые правила, порядок случайности.
 * История (коммит — что изменилось; ветки в коде: reb(G, from, to) и rv(G)):
 *  1  ba4f8f3 29.08 волна 1: цены + Виагрус без палочки из стопки, Гусыня ≤6, дохляки и Некрошест ×1 по всем жетонам
 *  2  afac70d 29.08 волна 2: Трондец 8, Капитан 7; План 1 карта, Парашют 1 чипсина, Зад +1 карта, Некрофея 5
 *  3  c034d37 29.08 волна 3: Овца 3; Цирк 6, Мальчик +1, Некрошест ×2 только за настоящие жетоны
 *  4  5109635 31.08 волна 4: пакет цен по контрфактике; Гусыня ≤10; свойства: приз 22 жизни, ☀6 две чипсины,
 *                          тварь-на-верх чипсина, ☀10 при одном волшебнике/заклинании
 *  5  f4f5363 31.08 волна 4.1: Капитан 8, Сделка 16, Виабу 8; ☀10 снова при двух, но с чипсиной
 *  6  9814d44 31.08 базовые правила: лошара воскресает с 15 жизнями, а не с 20 (и в обычных партиях)
 *  7  5fc7c94 01.09 волна 4.2: Трондец 10, Братство 4, Виабу 10, Шериф/Зожложники/Нотари/Длань 8, Арена 12;
 *                          Виагрус +2 за палочку, Цирк 8, Гусыня ≤8
 *  8  85d941b 01.09 волна 4.3: приз — 25 жизней, но без стартового приза; волшебник-на-верх, хреналочка +2, постоянка 2 чипсины
 *  9  315c206 01.09 волна 4.4: Та самая вялая 13, Парк 12, Кондуктор 8, Маячок 15; Легендохранитель в защите 1+1;
 *                          приз возвращён, жизни 20, первый ход в режиме случайный
 * 10  a07e5ec 01.09 волна 4.5: Братья 14, Миниганочка 18, Никчемухажёры 8, Мальчишник 13, Лошарочка 8, Вурдалакар 5,
 *                          Вялая башня 2, баф Маголовки отменён; приз снова 25 жизней
 * 11          24.09 подготовка по правилам (стр. 4): сначала жетон свойства, и только потом — фамильяр
 *                          (его предложения видны после выбора жетона); см. stepwiseSetup
 * 12          24.09 Зад в будущее: купленную шальную магию тоже можно взять на руку (стопка шальной магии — часть
 *                          барахолки); см. chaosToHand
 * 13          25.09 автоматика ждёт, пока на столе постоянка, уничтожающая карты с руки (Волшебные отходы), и
 *                          игрок в этом ходу ещё ничего не сыграл; см. holdForTableTrash
 * 14          28.09 Зад в будущее: купленного своего фамильяра (из-под планшета) тоже можно взять на руку;
 *                          см. familiarToHand
 * 15          09.10 Цирк братьев Лошашных переворачивает ВЕСЬ штраф за жетон лошары — и удвоенный жетоном дохлого
 *                          колдуна «удвой штраф лошары» (было: −5 −5 +10 = 0, стало +10); см. circusFlipsAll
 * 16          09.10 перенаправленная атака убила атакующего — убийца тот, кто её перенаправил (правила, с. 17: урон
 *                          «обратно ему в лицо»): ему главный приз; раньше это считалось самоубийством; см. redirectKills
 * 17          09.10 автоматика не играет карты с добором, пока у игрока есть чем уничтожить карты с руки (Волшебные
 *                          отходы, Спиногрызогрызы): добор закрыл бы «Отмену», и хлам из руки было бы не вернуть;
 *                          см. holdRevealsForTrash
 * 18          09.10 полная ничья (равны ПО, легенды и ЖДК — правила, с. 9, дальше ничего не говорят): победа
 *                          засчитывается всем равным; раньше её молча получал сидящий раньше; см. sharedWin
 * 19          09.10 Венерина маголовка «и тем и другим»: жизни меняются такими, какими были до обмена статусом
 *                          (лошара с 15 против 20 → бывший лошара получает 20); раньше новый лошара сначала срезался до 15,
 *                          и обмен приносил 15; см. swapLivesBeforeCap
 * 20          09.10 «Отмена» по шагам: если после розыгрыша карты сработала автоматика, первая отмена возвращает
 *                          только сыгранное автоматикой, вторая — саму карту (раньше одна отмена откатывала всё разом,
 *                          и сыграть карту, которой нужна сыгранная автоматикой тварь, было нельзя); см. stepUndo.
 *                          Это не правило игры: интерфейс отменяет по шагам в любой партии (cancel с пометкой step), а
 *                          версия лишь говорит, как понимать отмену без пометки — старые журналы записаны без неё
 * 21          09.10 Смерть от холестерина: Дохляк на столе («считается жетоном дохлого колдуна») тоже можно уничтожить
 *                          как жетон; раньше при Дохляке и без настоящих жетонов карта не делала ничего; см. holesterinDohlyak
 * 22          24.10 мутаторы партии (G.mutators, src/engine/mutators.ts): «Жирная рука» — рука 6 карт. Партия без
 *                          мутаторов играется как прежде; версия поднята, чтобы записи с мутаторами не пробовались
 *                          старыми версиями, где мутаторов не было
 * 23          25.10 боссы (Player.boss, src/engine/bosses.ts): сложные боты с лицом колдуна-планшета, своим фамильяром
 *                          и одной пассивкой (скидки, урон палочек, рука, чипсины, хлам с верха колоды, штрафы). Партия
 *                          без боссов не меняется
 * 24          25.10 мутаторы «Хрупкие колдуны», «Богатая барахолка», «Наследство», «Догоняющий», «Беспредельный круг»
 *                          и правила дуэли с боссом (mutators.ts); партии без них не меняются
 * Партии до версии 1 (28.08, цены волны 1 без правок эффектов) не поддерживаются: записей с них нет.
 */
export const RULES_VERSION = 24;
/** Самая старая версия, которую движок умеет воспроизводить */
export const RULES_VERSION_MIN = 1;

/** Версия правил партии; партия без версии (сохранена до появления версий) считается текущей — см. ensureRulesVersion */
export const rv = (G: { rulesVersion?: number }): number => G.rulesVersion ?? RULES_VERSION;
/** Ветка правки режима ребаланса, действующая с версии from по версию to включительно */
export const reb = (G: { rulesVersion?: number; rebalance?: boolean }, from: number, to = Infinity): boolean => {
  if (!G.rebalance) return false;
  const v = rv(G);
  return v >= from && v <= to;
};

/** Подготовка в два шага (с v11): жетон свойства выбирается вслепую, фамильяр — уже зная жетон; до v11 — одним выбором */
export const stepwiseSetup = (G: { rulesVersion?: number }): boolean => rv(G) >= 11;

/** Зад в будущее и шальная магия (с v12): покупка из стопки шальной магии — тоже «с барахолки», можно взять на руку */
export const chaosToHand = (G: { rulesVersion?: number }): boolean => rv(G) >= 12;

/** Зад в будущее и фамильяр (с v14): купленного своего фамильяра из-под планшета можно взять на руку */
export const familiarToHand = (G: { rulesVersion?: number }): boolean => rv(G) >= 14;

/** Цирк братьев Лошашных (с v15): штраф лошары переворачивается целиком, вместе с удвоением от ЖДК k12 */
export const circusFlipsAll = (G: { rulesVersion?: number }): boolean => rv(G) >= 15;

/** Перенаправленная атака (с v16): если она убила атакующего, убийца — перенаправивший (приз, «если тебя убил…») */
export const redirectKills = (G: { rulesVersion?: number }): boolean => rv(G) >= 16;

/** Автоматика не играет карты с добором, пока доступно уничтожение карт с руки (с v17; см. T.HAND_TRASHERS) */
export const holdRevealsForTrash = (G: { rulesVersion?: number }): boolean => rv(G) >= 17;

/** Полная ничья в конце игры (с v18): равные по ПО, легендам и ЖДК побеждают вместе (G.winners) */
export const sharedWin = (G: { rulesVersion?: number }): boolean => rv(G) >= 18;

/** Венерина маголовка, обмен и статусом, и жизнями (с v19): жизни — те, что были до обмена статусом; потолок — после */
export const swapLivesBeforeCap = (G: { rulesVersion?: number }): boolean => rv(G) >= 19;

/** «Отмена» без пометки step — по шагам (с v20); до v20 она откатывает действие вместе с авто-розыгрышем (старые журналы) */
export const stepUndo = (G: { rulesVersion?: number }): boolean => rv(G) >= 20;

/** Смерть от холестерина (с v21): Дохляк, «считающийся жетоном дохлого колдуна», тоже можно уничтожить как жетон */
export const holesterinDohlyak = (G: { rulesVersion?: number }): boolean => rv(G) >= 21;

/** Автоматика ждёт первой сыгранной карты, пока на столе постоянка «уничтожь карты с руки» (с v13; см. T.TABLE_HAND_TRASH) */
export const holdForTableTrash = (G: { rulesVersion?: number }): boolean => rv(G) >= 13;

export const RULES = {
  /** жизни в начале и после смерти */
  startLife: 20,
  /** предел жизней обычного колдуна */
  maxLife: 25,
  /** предел жизней лошары */
  loserMaxLife: 15,
  /** размер руки при доборе */
  handSize: 5,
  marketSize: 5,
  legendMarketSize: 3,
  chaosPrice: 3,
  familiarPrice: 6,
  /** жетонов дохлых колдунов в стопке на каждого игрока */
  ddkPerPlayer: 4,
  /** базовый штраф за жетон дохлого колдуна; звезда на жетоне — добавка к нему */
  ddkBasePenalty: -3,
  loserPenalty: -5,
  /** штраф за вялую палочку */
  limpPenalty: -1,
  chipsSupply: 40,
  /** чипсин владельцу главного приза в конце его хода */
  prizeChips: 1,
  /** жизни владельца главного приза (жетон свойства «приз»): в начале и после смерти */
  prizeOwnerLife: 25,
  /** то же в режиме ребаланса версий 4–7 (правка снята в 8: срез не подействовал) */
  prizeOwnerLifeV4: 22,
} as const;

/** Стартовая колода: 6 знаков, сырная палочка, 3 пшика (жетон свойства «хреналочка» меняет один знак на палочку) */
export const STARTER_DECK = {
  sign: 'znak' as CardId,
  signs: 6,
  wand: 'syrnaya-palochka' as CardId,
  blank: 'pshik' as CardId,
  blanks: 3,
  hrenalochka: 'palochka-hrenalochka' as CardId,
} as const;

/** Карта из стопки шальной магии (одна на все экземпляры) */
export const CHAOS_CARD: CardId = 'shalnaya-magiya';
